"use client";

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useSession } from "next-auth/react";

export interface SavedCandidate {
  id: string | number;
  savedAt: string;
}

interface SavedCandidatesContextType {
  savedCandidates: SavedCandidate[];
  saveCandidate: (id: string | number) => void;
  removeCandidate: (id: string | number) => void;
  isSaved: (id: string | number) => boolean;
}

const WP_URL = "https://admin.myjobs.al/wp-json/myjobs/v1/user-data";

const SavedCandidatesContext = createContext<SavedCandidatesContextType | undefined>(undefined);

function normalizeId(id: string | number): string {
  let strId = String(id).trim();
  if (strId.length > 10 && /^[A-Za-z0-9+/=]+$/.test(strId)) {
    try {
      const decoded =
        typeof window !== "undefined" && window.atob
          ? window.atob(strId)
          : Buffer.from(strId, "base64").toString();
      if (decoded.includes(":")) {
        const parts = decoded.split(":");
        if (parts[1] && !isNaN(Number(parts[1]))) return String(parts[1]);
      }
    } catch (e) {}
  }
  return strId;
}

function parseWpJson(val: unknown): SavedCandidate[] {
  let v: unknown = val;
  while (typeof v === "string" && v.trim() !== "") {
    try {
      const p = JSON.parse(v);
      if (p === v) break;
      v = p;
    } catch (e) {
      break;
    }
  }
  return Array.isArray(v) ? (v as SavedCandidate[]) : [];
}

function getUserKeys(): {
  email: string | null;
  wpId: string | null;
  savedKey: string;
} {
  if (typeof window === "undefined")
    return {
      email: null,
      wpId: null,
      savedKey: "savedCandidates_guest",
    };
  const emailMatch = document.cookie.match(/(^| )userEmail=([^;]+)/);
  const email =
    localStorage.getItem("userEmail") ||
    (emailMatch ? decodeURIComponent(emailMatch[2]) : null);
  const idMatch = document.cookie.match(/(^| )wpUserId=([^;]+)/);
  const wpId =
    localStorage.getItem("wpUserId") || (idMatch ? idMatch[2] : null);
  const safeEmail = email
    ? email.toLowerCase().trim().replace(/[^a-z0-9]/g, "_")
    : null;
  const savedKey = safeEmail
    ? `savedCandidates_${safeEmail}`
    : wpId
    ? `savedCandidates_id_${wpId}`
    : "savedCandidates_guest";
  return { email, wpId, savedKey };
}

async function writeToWP(
  saved: SavedCandidate[],
  email: string | null,
  wpId: string | null
) {
  if (!email && !wpId) return;
  try {
    await fetch(WP_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: wpId,
        user_email: email,
        saved_candidates: saved,
      }),
    });
  } catch (e) {
    console.error("writeToWP failed:", e);
  }
}

async function readFromWP(
  email: string | null,
  wpId: string | null
): Promise<{ saved: SavedCandidate[] } | null> {
  if (!email && !wpId) return null;
  const parts: string[] = [];
  if (wpId) parts.push(`user_id=${encodeURIComponent(wpId)}`);
  if (email) parts.push(`user_email=${encodeURIComponent(email)}`);
  try {
    const res = await fetch(`${WP_URL}?${parts.join("&")}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      saved: parseWpJson(data.saved_candidates),
    };
  } catch (e) {
    console.error("readFromWP failed:", e);
    return null;
  }
}

export function SavedCandidatesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [savedCandidates, setSavedCandidates] = useState<SavedCandidate[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const { data: session, status } = useSession();

  const fetchedRef = useRef(false);

  const loadUserData = useCallback((email: string | null, wpId: string | null) => {
    if (!email && !wpId) return;
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const safeEmail = email ? email.toLowerCase().trim().replace(/[^a-z0-9]/g, '_') : null;
    const savedKey = safeEmail ? `savedCandidates_${safeEmail}` : `savedCandidates_id_${wpId}`;

    if (email && !localStorage.getItem('userEmail')) localStorage.setItem('userEmail', email);
    if (email) localStorage.setItem('isLoggedIn', 'true');
    if (wpId && !localStorage.getItem('wpUserId')) localStorage.setItem('wpUserId', String(wpId));

    const cached = localStorage.getItem(savedKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) setSavedCandidates(parsed);
      } catch (e) {}
    }

    readFromWP(email, wpId).then((result) => {
      if (!result) {
        return;
      }

      const localRaw = localStorage.getItem(savedKey);
      let localItems: SavedCandidate[] = [];
      try { localItems = localRaw ? JSON.parse(localRaw) : []; } catch (e) {}

      if (result.saved.length === 0 && localItems.length > 0) {
        writeToWP(localItems, email, wpId);
        setSavedCandidates(localItems);
        return;
      }

      setSavedCandidates(result.saved);
      try {
        localStorage.setItem(savedKey, JSON.stringify(result.saved));
      } catch (e) {}
    });

    const handleStorage = (e: StorageEvent) => {
      if (e.key === savedKey && e.newValue) {
        try { setSavedCandidates(JSON.parse(e.newValue)); } catch (e) {}
      }
    };
    window.addEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    setIsMounted(true);
    const { email, wpId } = getUserKeys();
    if (email || wpId) {
      loadUserData(email, wpId);
    }
  }, [loadUserData]);

  useEffect(() => {
    if (status !== 'authenticated' || !session) return;
    const sessionEmail = session.user?.email ?? null;
    const sessionWpId = String((session as any).wpUserId ?? '');
    loadUserData(sessionEmail, sessionWpId || null);
  }, [status, session, loadUserData]);

  const saveCandidate = useCallback(
    (id: string | number) => {
      const normId = normalizeId(id);
      const { email, wpId, savedKey } = getUserKeys();

      setSavedCandidates((prev) => {
        if (prev.some((c) => normalizeId(c.id) === normId)) return prev;
        const newCandidate: SavedCandidate = {
          id: normId,
          savedAt: new Date().toISOString(),
        };
        const next = [...prev, newCandidate];
        try {
          localStorage.setItem(savedKey, JSON.stringify(next));
        } catch (e) {}
        writeToWP(next, email, wpId);
        return next;
      });
    },
    []
  );

  const removeCandidate = useCallback(
    (id: string | number) => {
      const normId = normalizeId(id);
      const { email, wpId, savedKey } = getUserKeys();

      setSavedCandidates((prev) => {
        const next = prev.filter((c) => normalizeId(c.id) !== normId);
        try {
          localStorage.setItem(savedKey, JSON.stringify(next));
        } catch (e) {}
        writeToWP(next, email, wpId);
        return next;
      });
    },
    []
  );

  const isSaved = useCallback(
    (id: string | number) => {
      const normId = normalizeId(id);
      return savedCandidates.some((c) => normalizeId(c.id) === normId);
    },
    [savedCandidates]
  );

  return (
    <SavedCandidatesContext.Provider
      value={{
        savedCandidates: isMounted ? savedCandidates : [],
        saveCandidate,
        removeCandidate,
        isSaved,
      }}
    >
      {children}
    </SavedCandidatesContext.Provider>
  );
}

export function useSavedCandidates() {
  const context = useContext(SavedCandidatesContext);
  if (context === undefined) {
    throw new Error("useSavedCandidates must be used within a SavedCandidatesProvider");
  }
  return context;
}
